import { must, supabase } from "./supabase.js";

/**
 * Invitation links look like https://despejo.co/?invitacion=<token> (DES-74).
 * The token is captured as soon as the app loads — before the magic-link
 * sign-in, which may complete in another tab — and consumed once the user has
 * an MFA session (see useOrgs).
 */
const KEY = "despejo.invitation";

function read(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function write(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(KEY, token);
    else window.localStorage.removeItem(KEY);
  } catch {
    // storage blocked: the link simply has to be opened again after signing in
  }
}

export function captureInvitationFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const token = url.searchParams.get("invitacion");
  if (!token || !/^[0-9a-f]{48}$/.test(token)) return;
  write(token);
  url.searchParams.delete("invitacion");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
}

export interface InvitationOutcome {
  status: "accepted" | "failed";
  message?: string;
}

let lastOutcome: InvitationOutcome | null = null;
/** Result of the last automatic acceptance, shown once in Ajustes. */
export function takeInvitationOutcome(): InvitationOutcome | null {
  const o = lastOutcome;
  lastOutcome = null;
  return o;
}

/** Accept a captured invitation, if any. Never throws: a bad link must not break the app. */
export async function acceptPendingInvitation(): Promise<void> {
  const token = read();
  if (!token) return;
  try {
    must(await supabase.rpc("accept_invitation", { p_token: token }));
    lastOutcome = { status: "accepted" };
  } catch (e) {
    const m = (e as Error).message;
    lastOutcome = {
      status: "failed",
      message: /another e-mail/.test(m)
        ? "La invitación es para otro correo. Inicie sesión con el correo que recibió la invitación."
        : /not valid/.test(m)
          ? "La invitación ya fue usada, venció o fue revocada."
          : m,
    };
  }
  write(null);
}

captureInvitationFromUrl();
